<template>
    <div>
        <div v-if="chartData && chartData.length > 0">
            <!-- Metric Tabs -->
            <div class="nav nav-tabs mb-3">
                <button
                    v-for="metric in metrics"
                    :key="metric.key"
                    class="nav-link"
                    :class="{ active: selectedMetric === metric.key }"
                    @click="selectedMetric = metric.key"
                >
                    {{ metric.label }}
                </button>
            </div>

            <canvas ref="chartCanvas" height="200"></canvas>

            <div class="text-center mt-3">
                <p class="text-muted">
                    <strong>Chart shows:</strong> Bars = Your performance (%) vs neighbors | Line = Total opportunity in window<br>
                    <small>🟢 Green bars = Outperformed | 🔴 Red bars = Underperformed | 📈 High line = High opportunity period</small><br>
                    <small><em>Look for green bars when the line is high - you caught the spike! Click any bar to explore blocks.</em></small>
                </p>
            </div>
        </div>
        <div v-else class="alert alert-info">
            No chart data available. Please ensure you have entered a valid validator ID and slot.
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
    name: 'ValidatorPerformanceChart',
    props: {
        chartData: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            chart: null,
            selectedMetric: 'rewards',
            metrics: [
                { key: 'rewards', label: 'Rewards', unit: 'SOL', decimals: 6 },
                { key: 'jito', label: 'Jito Tips', unit: 'SOL', decimals: 6 },
                { key: 'revenue', label: 'Total Revenue', unit: 'SOL', decimals: 6 },
                { key: 'cu', label: 'Compute Units', unit: 'M', decimals: 2 },
                { key: 'user_txns', label: 'User Transactions', unit: '', decimals: 0 }
            ]
        };
    },
    computed: {
        currentMetric() {
            return this.metrics.find(m => m.key === this.selectedMetric);
        },
        currentMetricLabel() {
            const metric = this.currentMetric;
            return metric ? metric.label.toLowerCase() : '';
        }
    },
    mounted() {
        if (this.chartData && this.chartData.length > 0) {
            this.createChart();
        }
    },
    watch: {
        chartData(newData) {
            if (this.chart) {
                this.chart.destroy();
            }
            if (newData && newData.length > 0) {
                this.createChart();
            }
        },
        selectedMetric() {
            if (this.chart) {
                this.updateChart();
            }
        }
    },
    beforeUnmount() {
        if (this.chart) {
            this.chart.destroy();
        }
    },
    methods: {
        createChart() {
            const ctx = this.$refs.chartCanvas.getContext('2d');
            const data = this.getChartData();
            const self = this;

            this.chart = new Chart(ctx, {
                type: 'bar',
                data: data,
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false
                    },
                    plugins: {
                        title: {
                            display: true,
                            text: `Validator ${this.currentMetric.label} Performance Analysis`,
                            font: {
                                size: 16
                            }
                        },
                        legend: {
                            display: true,
                            position: 'top'
                        },
                        tooltip: {
                            callbacks: {
                                title: function(context) {
                                    const index = context[0].dataIndex;
                                    return 'Slots: ' + self.chartData[index].slot_range;
                                },
                                afterTitle: function(context) {
                                    const index = context[0].dataIndex;
                                    return 'Time: ' + self.chartData[index].block_time;
                                },
                                label: function(context) {
                                    const index = context.dataIndex;
                                    const dataPoint = self.chartData[index];
                                    const metric = self.currentMetric;
                                    const diffKey = metric.key + '_diff';
                                    const myKey = 'my_' + metric.key;
                                    const neighborsKey = 'neighbors_' + metric.key;
                                    const windowKey = 'window_' + metric.key;

                                    if (context.datasetIndex === 0) {
                                        // Performance dataset (bars)
                                        return [
                                            'Your Performance: ' + (dataPoint[diffKey] > 0 ? '+' : '') + dataPoint[diffKey] + '%',
                                            'Your Avg: ' + dataPoint[myKey] + (metric.unit ? ' ' + metric.unit : ''),
                                            'Neighbors Avg: ' + dataPoint[neighborsKey] + (metric.unit ? ' ' + metric.unit : '')
                                        ];
                                    } else {
                                        // Opportunity dataset (line)
                                        return [
                                            'Total Opportunity: ' + dataPoint[windowKey] + (metric.unit ? ' ' + metric.unit : ''),
                                            dataPoint[windowKey] > dataPoint[neighborsKey] * 2 ? '⚡ High activity period!' : ''
                                        ].filter(Boolean);
                                    }
                                },
                                footer: function() {
                                    return '🔗 Click to view in block explorer';
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            display: true,
                            title: {
                                display: true,
                                text: 'Block Time'
                            },
                            ticks: {
                                maxRotation: 45,
                                minRotation: 45
                            }
                        },
                        y: {
                            type: 'linear',
                            display: true,
                            position: 'left',
                            title: {
                                display: true,
                                text: 'Performance Difference (%)'
                            },
                            ticks: {
                                callback: function(value) {
                                    return value + '%';
                                }
                            },
                            grid: {
                                drawOnChartArea: true
                            }
                        },
                        y1: {
                            type: 'linear',
                            display: true,
                            position: 'right',
                            title: {
                                display: true,
                                text: `Total ${this.currentMetric.label} Opportunity${this.currentMetric.unit ? ' (' + this.currentMetric.unit + ')' : ''}`
                            },
                            ticks: {
                                callback: (value) => {
                                    // Only show positive values in labels, but allow negative scale
                                    if (value >= 0) {
                                        const decimals = this.currentMetric.decimals;
                                        return decimals > 0 ? value.toFixed(decimals) : Math.round(value);
                                    }
                                    return '';
                                }
                            },
                            grid: {
                                drawOnChartArea: false
                            },
                            // Align zero point with left axis
                            afterDataLimits: (scale) => {
                                // Get the percentage scale limits
                                const percentScale = scale.chart.scales.y;
                                if (percentScale) {
                                    const percentMin = percentScale.min;
                                    const percentMax = percentScale.max;

                                    // Calculate what proportion of the left scale is above zero
                                    const percentZeroPosition = Math.abs(percentMin) / (percentMax - percentMin);

                                    // Apply same proportion to value scale
                                    const valueMax = scale.max;
                                    const valueMin = -(valueMax * (percentZeroPosition / (1 - percentZeroPosition)));

                                    scale.min = valueMin;
                                    scale.max = valueMax;
                                }
                            }
                        }
                    },
                    onClick: (evt, elements) => {
                        if (elements && elements.length > 0) {
                            const index = elements[0].index;
                            const dataPoint = this.chartData[index];
                            const slotRange = dataPoint.slot_range.split('-');
                            // Expand range to include 4 slots before and 4 slots after
                            const startSlot = parseInt(slotRange[0]) - 4;
                            const endSlot = parseInt(slotRange[1]) + 4;
                            const url = `/blocks?filter%5Bslots%5D=${startSlot}%2C${endSlot}`;
                            window.open(url, '_blank');
                        }
                    }
                }
            });
        },
        updateChart() {
            if (!this.chart) return;

            // Destroy and recreate the chart to avoid update issues
            this.chart.destroy();
            this.createChart();
        },
        getChartData() {
            const metric = this.currentMetric;
            const labels = [];
            const performanceData = [];
            const windowTotalData = [];
            const backgroundColors = [];

            this.chartData.forEach(item => {
                // Use block time as label
                const timeLabel = item.block_time !== 'Unknown' ?
                    item.block_time.substring(5, 16) : // MM-DD HH:MM
                    item.label.toString();
                labels.push(timeLabel);

                const diffKey = metric.key + '_diff';
                const windowKey = 'window_' + metric.key;

                performanceData.push(item[diffKey]);
                windowTotalData.push(item[windowKey] || 0);
                // Green for positive, red for negative performance
                backgroundColors.push(item[diffKey] >= 0 ? 'rgba(40, 167, 69, 0.7)' : 'rgba(220, 53, 69, 0.7)');
            });

            return {
                labels: labels,
                datasets: [
                    {
                        label: 'Performance vs Neighbors (%)',
                        data: performanceData,
                        backgroundColor: backgroundColors,
                        borderColor: backgroundColors.map(c => c.replace('0.7', '1')),
                        borderWidth: 1,
                        yAxisID: 'y',
                        order: 2
                    },
                    {
                        label: `Total ${metric.label} in Window`,
                        data: windowTotalData,
                        type: 'line',
                        borderColor: 'rgba(255, 193, 7, 1)',
                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                        borderWidth: 2,
                        pointRadius: 1,
                        pointHoverRadius: 3,
                        pointBackgroundColor: 'rgba(255, 193, 7, 1)',
                        yAxisID: 'y1',
                        order: 1
                    }
                ]
            };
        }
    }
}
</script>

<style scoped>
canvas {
    max-height: 800px;
    height: 800px;
}
.nav-tabs .nav-link {
    cursor: pointer;
    color: #666;
    border: 1px solid #dee2e6;
    background-color: #f8f9fa;
}
.nav-tabs .nav-link.active {
    color: #495057;
    background-color: #fff;
    border-color: #dee2e6 #dee2e6 #fff;
}
.nav-tabs .nav-link:hover {
    border-color: #e9ecef #e9ecef #dee2e6;
}
</style>